import { useState, useEffect } from "react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [minRate, setMinRate] = useState("");

  useEffect(() => {
    adminApi.get("/admin/stats").then(({ data }) => { setStats(data); setMinRate(data.min_call_rate); }).catch(() => {});
  }, []);

  const saveMinRate = async () => {
    try {
      await adminApi.put("/admin/settings/calls", { min_call_rate: Number(minRate) });
      toast.success("Minimum call rate updated");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!stats) return <div className="grid grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-24 rounded-2xl" />)}</div>;

  const cards = [
    { label: "Total Users", value: stats.total_users, testId: "stat-users" },
    { label: "Live Streams Now", value: stats.live_streams, testId: "stat-live" },
    { label: "Gifts Sent Today", value: stats.gifts_sent_today, testId: "stat-gifts" },
    { label: "Revenue Today", value: `₹${(stats.revenue?.today?.inr || 0).toLocaleString()}`, testId: "stat-revenue" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-xl font-extrabold font-display mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testId} className="glass rounded-2xl p-5">
            <p className="text-zinc-500 text-xs font-semibold mb-1">{c.label}</p>
            <p className="text-2xl font-extrabold gradient-text">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="glass rounded-2xl p-5">
          <p className="text-sm font-bold mb-3">Revenue</p>
          {["today", "week", "month"].map((k) => (
            <div key={k} className="flex justify-between text-xs py-1.5 border-b border-white/5 last:border-0">
              <span className="text-zinc-400 capitalize">{k}</span>
              <span className="text-white font-bold">₹{(stats.revenue?.[k]?.inr || 0).toLocaleString()} · {(stats.revenue?.[k]?.coins || 0).toLocaleString()} coins</span>
            </div>
          ))}
        </div>
        <div className="glass rounded-2xl p-5">
          <p className="text-sm font-bold mb-3">Top Streamers ♦️</p>
          {stats.top_streamers?.map((u) => (
            <div key={u.id} className="flex items-center gap-2 py-1.5">
              <img src={u.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
              <span className="text-xs text-zinc-300 flex-1 truncate">{u.name}</span>
              <span className="text-xs text-cyan-300 font-bold">{u.diamonds?.toLocaleString()}</span>
            </div>
          ))}
        </div>
        <div className="glass rounded-2xl p-5">
          <p className="text-sm font-bold mb-3">Top Spenders 🥇</p>
          {stats.top_spenders?.map((u) => (
            <div key={u.id} className="flex items-center gap-2 py-1.5">
              <img src={u.avatar || "https://i.pravatar.cc/100?u=x"} alt="" className="w-6 h-6 rounded-full object-cover" />
              <span className="text-xs text-zinc-300 flex-1 truncate">{u.name || u.phone}</span>
              <span className="text-xs text-amber-400 font-bold">{u.xp?.toLocaleString()} XP</span>
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-5 mb-6 flex items-end gap-4">
        <div>
          <p className="text-sm font-bold mb-1">1:1 Call Settings</p>
          <label className="text-[10px] text-zinc-500 font-semibold block mb-1">Minimum rate (coins/min) streamers can set</label>
          <input data-testid="min-call-rate-input" type="number" min={1} value={minRate} onChange={(e) => setMinRate(e.target.value)}
            className="w-28 bg-[#1A1A24] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none" />
        </div>
        <button data-testid="min-call-rate-save" onClick={saveMinRate} className="gradient-primary text-white text-xs font-bold px-5 py-2.5 rounded-full">Save</button>
      </div>

      <div className="glass rounded-2xl p-5">
        <p className="text-sm font-bold mb-3">Recent Transactions</p>
        <table className="w-full text-xs">
          <thead><tr className="text-zinc-500 text-left"><th className="pb-2">User</th><th>Type</th><th>Amount</th><th>Time</th></tr></thead>
          <tbody>
            {stats.recent_transactions?.map((t) => (
              <tr key={t.id} className="border-t border-white/5">
                <td className="py-2 text-zinc-300">{t.user}</td>
                <td className="text-zinc-400">{t.type}</td>
                <td className={t.amount >= 0 ? "text-emerald-400 font-bold" : "text-rose-400 font-bold"}>{t.amount.toLocaleString()} {t.currency}</td>
                <td className="text-zinc-500">{new Date(t.created_at).toLocaleTimeString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
